/**
 * Dashboard Absences — KPI + répartition département/type
 */
import { useState, useEffect, useCallback } from 'react';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { BarChart3, Users, AlertTriangle, Clock, TrendingDown, FileX } from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';

function KpiCard({ icon: Icon, label, value, sub, color = 'text-indigo-600' }) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center gap-3">
          <div className={`p-2.5 rounded-lg bg-slate-100 ${color}`}>
            <Icon className="w-5 h-5" />
          </div>
          <div className="min-w-0">
            <p className="text-xs text-slate-500">{label}</p>
            <p className="text-2xl font-bold text-slate-900">{value}</p>
            {sub && <p className="text-xs text-slate-500 mt-0.5">{sub}</p>}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function AbsencesDashboardPage() {
  const [range, setRange] = useState(90);
  const [kpis, setKpis] = useState(null);
  const [byDept, setByDept] = useState([]);
  const [byType, setByType] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchAll = useCallback(async () => {
    try {
      setLoading(true);
      const [k, d, t] = await Promise.all([
        api.get(`/leave/dashboard/kpis?range_days=${range}`),
        api.get(`/leave/dashboard/by-department?range_days=${range}`),
        api.get(`/leave/dashboard/by-type?range_days=${range}`),
      ]);
      setKpis(k.data);
      setByDept(d.data?.items || []);
      setByType(t.data?.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    } finally { setLoading(false); }
  }, [range]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const maxDept = Math.max(1, ...byDept.map(d => d.totalDays));
  const maxType = Math.max(1, ...byType.map(t => t.totalDays));

  return (
    <DashboardLayout>
      <div className="p-6 max-w-7xl mx-auto" data-testid="absences-dashboard-page">
        <div className="flex items-start justify-between mb-6 flex-wrap gap-2">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <BarChart3 className="w-6 h-6 text-indigo-600" />
              Tableau de bord Absences
            </h1>
            <p className="text-sm text-slate-500 mt-1">Indicateurs RH consolidés sur la période sélectionnée.</p>
          </div>
          <div className="flex gap-2">
            {[30, 90, 180, 365].map(r => (
              <Button key={r} size="sm" variant={range === r ? 'default' : 'outline'} onClick={() => setRange(r)}>
                {r}j
              </Button>
            ))}
          </div>
        </div>

        {loading || !kpis ? <p className="text-sm text-slate-400 text-center py-8">Chargement...</p> : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
              <KpiCard icon={Users} label="Effectif actif" value={kpis.totalEmployees} />
              <KpiCard icon={TrendingDown} label="Taux d'absentéisme" value={`${kpis.absenceRatePercent}%`} color="text-red-600" />
              <KpiCard icon={Clock} label="Jours d'absence" value={kpis.totalDaysAbsent} sub={`sur ${range} jours`} />
              <KpiCard icon={BarChart3} label="Demandes approuvées" value={kpis.approvedRequestsInRange} color="text-emerald-600" />
              <KpiCard icon={AlertTriangle} label="En attente" value={kpis.pendingRequests} color="text-amber-600" />
              <KpiCard icon={FileX} label="Non justifiées" value={kpis.unjustifiedAbsences} color="text-red-600" />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Card>
                <CardContent className="p-4">
                  <h3 className="font-semibold mb-3">Top départements concernés</h3>
                  {byDept.length === 0 ? (
                    <p className="text-sm text-slate-400">Aucune donnée.</p>
                  ) : (
                    <div className="space-y-2">
                      {byDept.slice(0, 8).map(d => (
                        <div key={d.departmentId} data-testid={`dept-bar-${d.departmentId}`}>
                          <div className="flex items-center justify-between text-xs mb-1">
                            <span className="truncate">{d.departmentName}</span>
                            <strong>{d.totalDays} j</strong>
                          </div>
                          <div className="h-2 bg-slate-100 rounded">
                            <div className="h-2 rounded bg-indigo-500" style={{ width: `${(d.totalDays / maxDept) * 100}%` }} />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-4">
                  <h3 className="font-semibold mb-3">Répartition par type d'absence</h3>
                  {byType.length === 0 ? (
                    <p className="text-sm text-slate-400">Aucune donnée.</p>
                  ) : (
                    <div className="space-y-2">
                      {byType.slice(0, 8).map(t => (
                        <div key={t.leaveTypeId} data-testid={`type-bar-${t.leaveTypeId}`}>
                          <div className="flex items-center justify-between text-xs mb-1">
                            <span className="flex items-center gap-1.5 truncate">
                              <span className="w-2.5 h-2.5 rounded" style={{ backgroundColor: t.leaveType?.color || '#94a3b8' }} />
                              {t.leaveType?.name}
                            </span>
                            <strong>{t.totalDays} j</strong>
                          </div>
                          <div className="h-2 bg-slate-100 rounded">
                            <div className="h-2 rounded" style={{ width: `${(t.totalDays / maxType) * 100}%`, backgroundColor: t.leaveType?.color || '#6366f1' }} />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </>
        )}
      </div>
    </DashboardLayout>
  );
}
